import { useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { TTaskResearchStartError, useGetTaskResearch, useStartTaskResearch } from '../../services/api/tasks.hooks'
import { Border, Colors, Spacing, Typography } from '../../styles'
import { tokens } from '../../styles/tokens'
import { TTaskV4, isTaskResearchActive } from '../../utils/types'
import { stopKeydownPropogation } from '../../utils/utils'
import GTButton from '../atoms/buttons/GTButton'

const Card = styled.section`
    padding: ${Spacing._12};
    border: ${Border.stroke.medium} solid ${Colors.background.hairline};
    border-radius: ${tokens.radius.md};
    background: ${Colors.background.white};
`

const Header = styled.header`
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: ${Spacing._24};
    color: ${Colors.text.title};
    ${Typography.title.small};
`

const Content = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${Spacing._8};
    padding-top: ${Spacing._8};
`

const Question = styled.textarea`
    box-sizing: border-box;
    width: 100%;
    min-height: ${Spacing._56};
    resize: vertical;
    padding: ${Spacing._8} ${Spacing._12};
    border: ${Border.stroke.medium} solid ${Colors.background.border};
    border-radius: ${tokens.radius.sm};
    background: ${Colors.background.white};
    color: ${Colors.text.ink};
    font-family: inherit;
    ${Typography.body.medium};
    ${Typography.touchFieldFloor};
    &:focus {
        border-color: ${Colors.text.ink};
    }
    &:focus:not(:focus-visible) {
        outline: none;
    }
    &:focus-visible {
        outline: ${tokens.focus.width} solid ${tokens.focus.ring};
        outline-offset: ${tokens.focus.offset};
    }
    &:disabled {
        border-color: ${Colors.background.hairline};
        background: ${Colors.background.base};
        color: ${Colors.text.base};
        resize: none;
    }
`

const Footer = styled.footer`
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: ${Spacing._8};
    padding-top: ${Spacing._8};
`

const Status = styled.div`
    display: flex;
    align-items: center;
    gap: ${Spacing._4};
    color: ${Colors.text.muted};
    font-weight: 400;
    ${Typography.body.small};
`

const ProgressMark = styled.span`
    width: 8px;
    height: 8px;
    border: ${Border.stroke.medium} solid ${Colors.background.hover};
    border-top-color: ${Colors.text.muted};
    border-radius: 50%;
    animation: research-spin 900ms linear infinite;
    @keyframes research-spin {
        to {
            transform: rotate(360deg);
        }
    }
`

const ErrorText = styled.div`
    color: ${Colors.semantic.warning.base};
    ${Typography.body.small};
`

const Result = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${Spacing._8};
    margin-top: ${Spacing._4};
    padding-top: ${Spacing._12};
    border-top: ${Border.stroke.medium} solid ${Colors.background.hairline};
`

const ResultLabel = styled.div`
    color: ${Colors.text.muted};
    ${Typography.label.small};
`

const Answer = styled.div`
    color: ${Colors.text.base};
    white-space: pre-wrap;
    ${Typography.body.medium};
`

const Sources = styled.ol`
    display: flex;
    flex-direction: column;
    gap: ${Spacing._2};
    margin: 0;
    padding-left: ${Spacing._16};
    ${Typography.body.small};
    a {
        color: ${tokens.intent.info};
        overflow-wrap: anywhere;
    }
`

const defaultQuestion = (title: string) => `What should I know before I work on “${title}”?`

const errorCopy = (code?: string) => {
    if (code === 'timeout') return 'Research took too long. Try again.'
    if (code === 'setup') return 'Research is unavailable. Try again later.'
    if (code === 'provider_unavailable') return 'Research is temporarily unavailable. Try again later.'
    if (code === 'incomplete') return 'Research reached its response limit. Try a narrower question.'
    if (code === 'empty_result') return 'No cited result was returned. Try a more specific question.'
    if (code === 'interrupted') return 'Research was interrupted. Try again.'
    return 'Research couldn’t finish. Try again.'
}

const startErrorCopy = (error: TTaskResearchStartError | null) => {
    if (error?.code === 'daily_limit' || error?.status === 429) return 'Daily research limit reached.'
    if (error?.code === 'active') return 'Research is already running.'
    if (error?.code === 'capacity' || error?.status === 409) return 'Research capacity reached.'
    return 'Couldn’t update research.'
}

interface TaskResearchCardProps {
    task: TTaskV4
    researchEnabled: boolean
}

const TaskResearchCard = ({ task, researchEnabled }: TaskResearchCardProps) => {
    const [expanded, setExpanded] = useState(!!task.research)
    const [editingNew, setEditingNew] = useState(!task.research)
    const [question, setQuestion] = useState(task.research?.question ?? defaultQuestion(task.title))
    const [poll, setPoll] = useState(researchEnabled && isTaskResearchActive(task.research))
    const researchQuery = useGetTaskResearch(task.id, researchEnabled && poll)
    const start = useStartTaskResearch(task.id)
    const research = task.research
    const researchIDBeforeStart = useRef(research?.id)

    useEffect(() => {
        if (!researchEnabled) {
            setPoll(false)
            return
        }
        if (isTaskResearchActive(research)) setPoll(true)
        else if (research) setPoll(false)
    }, [researchEnabled, research?.id, research?.status])

    useEffect(() => {
        // A disconnected POST can still have created the durable job. Read the task's saved state
        // before offering another start, which would otherwise collide with that active run.
        if (research && start.isError && research.id !== researchIDBeforeStart.current) {
            start.reset()
            setEditingNew(false)
        }
    }, [research?.id, research?.status, start.isError])

    useEffect(() => {
        if (research && !editingNew) setQuestion(research.question)
    }, [research?.id, editingNew])

    const isWorking = isTaskResearchActive(research) || start.isLoading
    const isAtDailyLimit = start.error?.code === 'daily_limit' || start.error?.status === 429
    const isFailed = !editingNew && research?.status === 'failed'
    const isComplete = !editingNew && research?.status === 'completed'
    const displayedResult = research?.status === 'completed' ? research : research?.retained
    const sources = useMemo(() => displayedResult?.sources ?? [], [displayedResult?.sources])
    const result = displayedResult && (
        <Result>
            {!isComplete && <ResultLabel>Last result</ResultLabel>}
            <Answer data-testid="task-research-answer">{displayedResult.answer}</Answer>
            <div>
                <ResultLabel>Sources</ResultLabel>
                <Sources data-testid="task-research-sources">
                    {sources.map((source) => (
                        <li key={source.url}>
                            <a href={source.url} target="_blank" rel="noreferrer">
                                {source.title}
                            </a>
                        </li>
                    ))}
                </Sources>
            </div>
        </Result>
    )

    // Turning Research off blocks new work, but completed citations remain part of the task's saved
    // history. Active/failed-only states have no read-only result to show and stay out of the UI.
    if (!researchEnabled) {
        if (!result) return null
        return (
            <Card data-testid="task-research-card" aria-label="Task research">
                <Header>Research</Header>
                <Content>{result}</Content>
            </Card>
        )
    }

    if (!research && !expanded) {
        return (
            <GTButton
                data-testid="task-research-trigger"
                styleType="control"
                value="Research"
                onClick={() => setExpanded(true)}
            />
        )
    }

    const begin = () => {
        const trimmed = question.trim()
        if (!trimmed || isWorking) return
        researchIDBeforeStart.current = research?.id
        start.mutate(trimmed, {
            onSuccess: (created) => {
                setEditingNew(false)
                setPoll(isTaskResearchActive(created))
            },
            onError: (error) => {
                if (error.code !== 'daily_limit' && error.status !== 429) setPoll(true)
            },
        })
    }

    const researchAgain = () => {
        setQuestion(research?.question ?? defaultQuestion(task.title))
        setEditingNew(true)
    }

    const cancelResearchAgain = () => {
        if (!research) return
        setQuestion(research.question)
        setEditingNew(false)
        start.reset()
    }

    return (
        <Card data-testid="task-research-card" aria-label="Task research">
            <Header>
                Research
                {isWorking && (
                    <Status role="status">
                        <ProgressMark aria-hidden /> Researching…
                    </Status>
                )}
            </Header>
            <Content>
                <Question
                    aria-label="Research question"
                    value={question}
                    maxLength={500}
                    disabled={isWorking || isComplete}
                    onChange={(event) => setQuestion(event.target.value)}
                    onKeyDown={(event) => stopKeydownPropogation(event)}
                />
                {(start.isError || researchQuery.isError) && (
                    <ErrorText role="alert">
                        {start.isError ? startErrorCopy(start.error) : 'Couldn’t update research.'}
                    </ErrorText>
                )}
                {isFailed && !start.isError && !researchQuery.isError && (
                    <ErrorText role="alert">{errorCopy(research?.error_code)}</ErrorText>
                )}
                {result}
            </Content>
            <Footer>
                {!research && !isWorking && (
                    <GTButton styleType="control" value="Not now" onClick={() => setExpanded(false)} />
                )}
                {isComplete && <GTButton styleType="control" value="Research again" onClick={researchAgain} />}
                {editingNew && research?.status === 'completed' && (
                    <GTButton styleType="control" value="Cancel" onClick={cancelResearchAgain} />
                )}
                {!isComplete && (
                    <GTButton
                        styleType="primary"
                        value={
                            isAtDailyLimit
                                ? 'Limit reached'
                                : isFailed
                                ? 'Try again'
                                : isWorking
                                ? 'Researching…'
                                : 'Research'
                        }
                        disabled={isWorking || isAtDailyLimit || !question.trim()}
                        onClick={begin}
                    />
                )}
            </Footer>
        </Card>
    )
}

export default TaskResearchCard
